import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QtClose, QtMenu } from "../../components/icons/QtIcon";
import {
  getCandidateDisplayName,
  isCandidateLoggedIn,
  signOutCandidate,
} from "../../utils/candidateAuth";
import CareersFooter from "../../components/CareersFooter";
import "../landing.scss";
import "./jobs.scss";

export const EMPLOYMENT_LABELS = {
  full_time: "Full time",
  part_time: "Part time",
  contract: "Contract",
  commission_only: "Commission only",
};

export const DEPARTMENT_LABELS = {
  sales: "Sales",
  logistics_admin: "Logistics admin",
  operations: "Operations",
  support: "Support",
  other: "Other",
};

export const APPLICATION_STATUS = {
  draft: { label: "Draft", tone: "slate" },
  submitted: { label: "Sent", tone: "orange" },
  shortlisted: { label: "Shortlisted", tone: "green" },
  interview: { label: "Interview", tone: "green" },
  hired: { label: "Hired", tone: "green" },
  rejected: { label: "Not selected", tone: "slate" },
  withdrawn: { label: "Withdrawn", tone: "slate" },
};

export function JobPill({ tone = "slate", children }) {
  return <span className={`job-pill job-pill--${tone}`}>{children}</span>;
}

export function EmploymentPill({ type }) {
  const tone = type === "commission_only" ? "orange" : type === "part_time" ? "green" : "slate";
  return <JobPill tone={tone}>{EMPLOYMENT_LABELS[type] || type}</JobPill>;
}

/** Simple paragraph and bullet renderer for plain text descriptions. No HTML is injected. */
export function JobText({ text }) {
  if (!text) return null;
  const blocks = String(text).split(/\n{2,}/);
  return (
    <>
      {blocks.map((block, index) => {
        const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
        const isList = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
        if (isList) {
          return (
            <ul key={index} className="job-text__list">
              {lines.map((line) => (
                <li key={line}>{line.replace(/^[-*]\s+/, "")}</li>
              ))}
            </ul>
          );
        }
        // A short single line with no end punctuation reads as a sub heading.
        if (lines.length === 1 && lines[0].length <= 40 && !/[.:;,!?]$/.test(lines[0])) {
          return (
            <h3 key={index} className="job-text__h">
              {lines[0]}
            </h3>
          );
        }
        return (
          <p key={index} className="job-text__p">
            {lines.join(" ")}
          </p>
        );
      })}
    </>
  );
}

export function formatClosing(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" });
}

export function JobsShell({ children }) {
  const [navOpen, setNavOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [displayName, setDisplayName] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoggedIn(isCandidateLoggedIn());
    setDisplayName(getCandidateDisplayName());
  }, []);

  const closeNav = () => setNavOpen(false);

  return (
    <div className="careers-landing jobs-page">
      <div className="careers-landing__bg" aria-hidden="true" />
      <header className="careers-nav">
        <div className="careers-nav__inner">
          <Link to="/" className="careers-nav__brand" onClick={closeNav}>
            QikTruck <span>Careers</span>
          </Link>
          <button
            type="button"
            className="careers-nav__toggle"
            aria-label={navOpen ? "Close menu" : "Open menu"}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          >
            {navOpen ? <QtClose /> : <QtMenu />}
          </button>
          <nav className={`careers-nav__links${navOpen ? " careers-nav__links--open" : ""}`}>
            <Link to="/jobs" className="careers-nav__link" onClick={closeNav}>
              Open positions
            </Link>
            <Link to="/academy" className="careers-nav__link" onClick={closeNav}>
              Training
            </Link>
            <Link to="/creator" className="careers-nav__link" onClick={closeNav}>
              Creator Program
            </Link>
            {loggedIn ? (
              <>
                {displayName ? (
                  <p className="careers-nav__signed-in">
                    Signed in as <strong>{displayName}</strong>
                  </p>
                ) : null}
                <Link to="/me/applications" className="careers-btn careers-btn--primary" onClick={closeNav}>
                  My applications
                </Link>
                <button
                  type="button"
                  className="careers-btn careers-btn--ghost careers-nav__signout"
                  onClick={() => {
                    closeNav();
                    signOutCandidate({ redirectTo: "/jobs" });
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link
                to={`/login?next=${encodeURIComponent("/me/applications")}`}
                className="careers-btn careers-btn--primary"
                onClick={closeNav}
              >
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </header>
      {children}
      <CareersFooter />
    </div>
  );
}
